/**
 * Admin App with Code Splitting
 * Phase 5.1.1: React.lazy for performance optimization
 * Phase 5.5.1: Error Boundary integration
 */

import { Suspense, lazy } from 'react';
import { Routes, Route } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { MainLayout } from './layouts/MainLayout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Loading } from './components/Loading';
import { ErrorBoundary } from './components/ErrorBoundary';

// Lazy load pages for code splitting
const Dashboard = lazy(() => import('./pages/Dashboard').then((m) => ({ default: m.Dashboard })));
const Tenants = lazy(() => import('./pages/Tenants').then((m) => ({ default: m.Tenants })));
const TenantDetail = lazy(() =>
  import('./features/tenants').then((m) => ({ default: m.TenantDetail }))
);
const TenantEditForm = lazy(() =>
  import('./features/tenants').then((m) => ({ default: m.TenantEditForm }))
);
const Roadmap = lazy(() => import('./pages/Roadmap').then((m) => ({ default: m.Roadmap })));
const FeatureFlags = lazy(() =>
  import('./pages/FeatureFlags').then((m) => ({ default: m.FeatureFlags }))
);
const ApiKeys = lazy(() => import('./pages/ApiKeys').then((m) => ({ default: m.ApiKeys })));
const Analytics = lazy(() => import('./pages/Analytics').then((m) => ({ default: m.Analytics })));
const Settings = lazy(() => import('./pages/Settings').then((m) => ({ default: m.Settings })));
const EmailTemplates = lazy(() =>
  import('./pages/EmailTemplates').then((m) => ({ default: m.EmailTemplates }))
);
const Webhooks = lazy(() => import('./pages/Webhooks').then((m) => ({ default: m.Webhooks })));
const ApiDocs = lazy(() => import('./pages/ApiDocs').then((m) => ({ default: m.ApiDocs })));
const Login = lazy(() => import('./pages/Login').then((m) => ({ default: m.Login })));

/**
 * Loading fallback component for Suspense
 */
function PageLoadingFallback() {
  return (
    <div className="flex h-full items-center justify-center">
      <Loading />
    </div>
  );
}

function App() {
  const { isLoading } = useAuth0();

  if (isLoading) {
    return <Loading />;
  }

  return (
    <ErrorBoundary>
      <Routes>
        <Route
          path="/login"
          element={
            <Suspense fallback={<PageLoadingFallback />}>
              <Login />
            </Suspense>
          }
        />
        <Route
          path="/*"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Suspense fallback={<PageLoadingFallback />}>
                  <Routes>
                    <Route path="/" element={<Dashboard />} />
                    <Route path="/tenants" element={<Tenants />} />
                    <Route path="/tenants/:tenantId" element={<TenantDetail />} />
                    <Route path="/tenants/:tenantId/edit" element={<TenantEditForm />} />
                    <Route path="/roadmap" element={<Roadmap />} />
                    <Route path="/feature-flags" element={<FeatureFlags />} />
                    <Route path="/api-keys" element={<ApiKeys />} />
                    <Route path="/webhooks" element={<Webhooks />} />
                    <Route path="/analytics" element={<Analytics />} />
                    <Route path="/email-templates" element={<EmailTemplates />} />
                    <Route path="/api-docs" element={<ApiDocs />} />
                    <Route path="/settings" element={<Settings />} />
                  </Routes>
                </Suspense>
              </MainLayout>
            </ProtectedRoute>
          }
        />
      </Routes>
    </ErrorBoundary>
  );
}

export default App;
